import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import SubcontractorAcknowledgementGate
  from "../components/SubcontractorAcknowledgementGate";

const API =
  "/api/contractor-onboarding/public/onboarding";

const BOOTSTRAP_URL =
  "/api/contractor-onboarding/public/invitation/bootstrap";

const SESSION_KEY =
  "iris_contractor_onboarding_session";

const STEPS = [
  "candidate_profile",
  "licences",
  "compliance",
  "declarations",
  "review",
  "completed",
];

const STEP_LABELS = {
  candidate_profile: "Your details",
  licences: "Licences",
  compliance: "Compliance",
  declarations: "Acknowledgement & Indemnity",
  review: "Review",
  completed: "Completed",
};

const initialProfile = {
  legal_name: "",
  preferred_name: "",
  mobile_phone: "",
  residential_address: "",
  suburb: "",
  state: "NSW",
  postcode: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
};

const initialLicences = {
  security_licence_number: "",
  security_licence_state: "NSW",
  security_licence_expiry: "",
  drivers_licence_held: false,
  drivers_licence_number: "",
  first_aid_current: false,
  first_aid_expiry: "",
};

const initialCompliance = {
  right_to_work_confirmed: false,
  willing_to_follow_site_rules: false,
  willing_to_follow_whs_requirements: false,
  fit_for_assigned_duties: false,
};

const initialDeclarations = {
  information_true_and_correct: false,
  consent_to_verification: false,
  consent_to_contact: false,
  agree_to_company_policies: false,
  declaration_name: "",
};

/*
 * IRIS_R7_R3_ACK_REQUEST
 *
 * Load the authoritative executed acknowledgement using
 * the active candidate onboarding session.
 */
async function reviewAcknowledgementRequest(
  sessionToken
) {
  if (!sessionToken) {
    throw new Error(
      "The secure onboarding session is unavailable."
    );
  }

  const response = await fetch(
    "/api/contractor-onboarding/public/onboarding" +
      "/acknowledgement/status",
    {
      method: "POST",
      headers: {
        "X-Onboarding-Session": sessionToken,
      },
      credentials: "same-origin",
    }
  );

  let data = null;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const error = new Error(
      data?.detail ||
      "The executed acknowledgement could not be loaded."
    );

    error.status = response.status;
    throw error;
  }

  return data;
}


function invitationToken() {
  const hash = window.location.hash || "";

  if (!hash) return null;

  const raw = hash.startsWith("#")
    ? hash.substring(1)
    : hash;

  return new URLSearchParams(raw).get("token");
}

function clearInvitationFragment() {
  window.history.replaceState(
    null,
    document.title,
    window.location.pathname + window.location.search
  );
}

async function jsonRequest(url, payload) {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  let data = null;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok) {
    const detail =
      data?.detail ||
      "The request could not be completed.";

    const error = new Error(
      typeof detail === "string"
        ? detail
        : JSON.stringify(detail)
    );

    error.status = response.status;

    throw error;
  }

  return data;
}

function fieldStyle() {
  return {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 13px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    fontSize: "15px",
    background: "#fff",
    color: "#111827",
    outline: "none",
  };
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <label
      style={{
        display: "block",
        marginBottom: "17px",
      }}
    >
      <div
        style={{
          fontWeight: 600,
          fontSize: "14px",
          marginBottom: "7px",
          color: "#374151",
        }}
      >
        {label}
        {required && (
          <span style={{ color: "#b91c1c" }}> *</span>
        )}
      </div>

      <input
        type={type}
        value={value ?? ""}
        onChange={(event) =>
          onChange(event.target.value)
        }
        placeholder={placeholder}
        required={required}
        style={fieldStyle()}
      />
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  required = false,
}) {
  return (
    <label
      style={{
        display: "block",
        marginBottom: "17px",
      }}
    >
      <div
        style={{
          fontWeight: 600,
          fontSize: "14px",
          marginBottom: "7px",
          color: "#374151",
        }}
      >
        {label}
        {required && (
          <span style={{ color: "#b91c1c" }}> *</span>
        )}
      </div>

      <textarea
        value={value ?? ""}
        onChange={(event) =>
          onChange(event.target.value)
        }
        required={required}
        rows={4}
        style={{
          ...fieldStyle(),
          resize: "vertical",
        }}
      />
    </label>
  );
}

function Check({
  label,
  checked,
  onChange,
}) {
  return (
    <label
      style={{
        display: "flex",
        gap: "11px",
        alignItems: "flex-start",
        marginBottom: "15px",
        cursor: "pointer",
        lineHeight: 1.5,
        color: "#374151",
      }}
    >
      <input
        type="checkbox"
        checked={Boolean(checked)}
        onChange={(event) =>
          onChange(event.target.checked)
        }
        style={{
          marginTop: "4px",
          width: "17px",
          height: "17px",
        }}
      />

      <span>{label}</span>
    </label>
  );
}

function SectionTitle({
  title,
  description,
}) {
  return (
    <div style={{ marginBottom: "24px" }}>
      <h2
        style={{
          margin: "0 0 7px",
          fontSize: "23px",
          color: "#111827",
        }}
      >
        {title}
      </h2>

      {description && (
        <div
          style={{
            color: "#6b7280",
            lineHeight: 1.55,
            fontSize: "14px",
          }}
        >
          {description}
        </div>
      )}
    </div>
  );
}

function UploadBox({
  sessionToken,
  documentType,
  title,
  description,
  required = false,
  onUploaded,
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function upload(event) {
    const file = event.target.files?.[0];

    if (!file) return;

    /*
     * IRIS V1.0C-H-R1-R7-R2
     *
     * Keep browser, Nginx and backend evidence limits aligned.
     * Backend MAX_EVIDENCE_BYTES = 10 * 1024 * 1024.
     */
    const MAX_EVIDENCE_BYTES =
      10 * 1024 * 1024;

    if (
      file.size >
      MAX_EVIDENCE_BYTES
    ) {
      setMessage(
        "Document exceeds the 10 MB upload limit."
      );
      event.target.value = "";
      return;
    }

    setBusy(true);
    setMessage("");

    try {
      const response = await fetch(
        `${API}/evidence/upload`,
        {
          method: "POST",
          headers: {
            "Content-Type":
              file.type || "application/octet-stream",
            "X-Onboarding-Session":
              sessionToken,
            "X-Document-Type":
              documentType,
            "X-Filename":
              file.name,
          },
          body: file,
        }
      );

      let data = null;

      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok) {
        throw new Error(
          data?.detail ||
          "Document upload failed."
        );
      }

      setMessage("Document uploaded successfully.");

      if (onUploaded) {
        await onUploaded();
      }
    } catch (error) {
      setMessage(
        error?.message ||
        "Document upload failed."
      );
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  }

  return (
    <div
      style={{
        border: "1px dashed #9ca3af",
        borderRadius: "10px",
        padding: "16px",
        marginBottom: "14px",
        background: "#f9fafb",
      }}
    >
      <div
        style={{
          fontWeight: 700,
          color: "#111827",
          marginBottom: "4px",
        }}
      >
        {title}
        {required && (
          <span style={{ color: "#b91c1c" }}> *</span>
        )}
      </div>

      <div
        style={{
          fontSize: "13px",
          color: "#6b7280",
          marginBottom: "11px",
          lineHeight: 1.5,
        }}
      >
        {description}
      </div>

      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
        disabled={busy}
        onChange={upload}
      />

      {message && (
        <div
          style={{
            marginTop: "9px",
            fontSize: "13px",
            color:
              message.includes("success")
                ? "#166534"
                : "#991b1b",
          }}
        >
          {message}
        </div>
      )}
    </div>
  );
}

export default function ContractorOnboardingInvitation() {
  const [status, setStatus] =
    useState("initialising");

  const [message, setMessage] = useState(
    "Preparing your secure onboarding session..."
  );

  const [sessionToken, setSessionToken] =
    useState(null);

  const [candidate, setCandidate] =
    useState(null);

  const [currentStep, setCurrentStep] =
    useState("candidate_profile");

  const [profile, setProfile] =
    useState(initialProfile);

  const [licences, setLicences] =
    useState(initialLicences);

  const [compliance, setCompliance] =
    useState(initialCompliance);

  const [declarations, setDeclarations] =
    useState(initialDeclarations);

  const [evidence, setEvidence] =
    useState([]);

  const [requirements, setRequirements] =
    useState([]);

  const [reviewData, setReviewData] =
    useState(null);

  const [
    reviewAcknowledgement,
    setReviewAcknowledgement,
  ] = useState(null);

  
  
const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  const activeIndex = Math.max(
    0,
    STEPS.indexOf(currentStep)
  );

  /*
   * IRIS_R7_R4_REVIEW_ACK_HYDRATION
   *
   * When a candidate reloads directly into the persisted
   * Review step, openReview() may not run again.
   *
   * Hydrate the authoritative executed acknowledgement
   * independently so Review remains complete after refresh.
   */
  useEffect(
    () => {
      let cancelled = false;

      async function hydrateReviewAcknowledgement() {
        if (
          currentStep !== "review" ||
          !sessionToken ||
          reviewAcknowledgement?.executed
        ) {
          return;
        }

        try {
          const acknowledgement =
            await reviewAcknowledgementRequest(
              sessionToken
            );

          if (cancelled) {
            return;
          }

          if (!acknowledgement?.executed) {
            setReviewAcknowledgement(
              acknowledgement || null
            );

            return;
          }

          setReviewAcknowledgement(
            acknowledgement
          );
        } catch (acknowledgementHydrationError) {
          if (cancelled) {
            return;
          }

          console.error(
            "Unable to hydrate Review acknowledgement:",
            acknowledgementHydrationError
          );

          setReviewAcknowledgement(
            null
          );
        }
      }

      hydrateReviewAcknowledgement();

      return () => {
        cancelled = true;
      };
    },
    [
      currentStep,
      sessionToken,
      reviewAcknowledgement?.executed,
    ]
  );


  const progress = useMemo(() => {
    if (currentStep === "completed") {
      return 100;
    }

    return Math.round(
      ((activeIndex + 1) /
        (STEPS.length - 1)) *
      100
    );
  }, [activeIndex, currentStep]);

  const expireSession = useCallback(() => {
    window.localStorage.removeItem(
      SESSION_KEY
    );

    setSessionToken(null);
    setStatus("error");
    setMessage(
      "Your secure onboarding session has expired or is unavailable."
    );
  }, []);

  const call = useCallback(
    async (path, payload = {}) => {
      try {
        return await jsonRequest(
          `${API}/${path}`,
          {
            session_token: sessionToken,
            ...payload,
          }
        );
      } catch (requestError) {
        if (requestError.status === 401) {
          expireSession();
        }

        throw requestError;
      }
    },
    [sessionToken, expireSession]
  );

  /*
   * IRIS V1.0C-H-R8-R3-R2-R1 AUTHORITATIVE REVIEW HYDRATION
   *
   * Browser bootstrap restores currentStep="review", but
   * reviewData was previously populated only by openReview().
   *
   * Therefore a hard refresh could render Review using blank
   * transient React state even though the persisted candidate
   * draft was complete.
   *
   * Whenever the active workflow step is Review, reload the
   * authoritative backend review projection.
   */
  useEffect(
    () => {
      if (
        !sessionToken ||
        currentStep !== "review"
      ) {
        return undefined;
      }

      let cancelled = false;

      async function hydrateAuthoritativeReview() {
        try {
          const result =
            await call("review");

          if (
            !result ||
            !result.canonical_review
          ) {
            throw new Error(
              "Saved onboarding review payload is unavailable."
            );
          }

          const savedProfile =
            result
              .canonical_review
              ?.candidate_profile;

          if (
            !savedProfile ||
            !savedProfile.legal_name
          ) {
            throw new Error(
              "Saved candidate profile was not returned by the review service."
            );
          }

          if (!cancelled) {
            setReviewData(
              result
            );
          }

        } catch (reviewLoadError) {
          if (!cancelled) {
            console.error(
              "IRIS authoritative Review hydration failed:",
              reviewLoadError
            );

            setReviewData(
              {
                __review_load_error:
                  reviewLoadError?.message ||
                  "Unable to load saved onboarding review.",
              }
            );
          }
        }
      }

      hydrateAuthoritativeReview();

      return () => {
        cancelled = true;
      };
    },
    [
      sessionToken,
      currentStep,
      call,
    ]
  );



  const refreshEvidence = useCallback(
    async (token = sessionToken) => {
      if (!token) return;

      try {
        const [list, policy] =
          await Promise.all([
            jsonRequest(
              `${API}/evidence/list`,
              {
                session_token: token,
              }
            ),
            jsonRequest(
              `${API}/evidence-requirements`,
              {
                session_token: token,
              }
            ),
          ]);

        setEvidence(
          Array.isArray(list)
            ? list
            : list?.evidence ||
              list?.documents ||
              []
        );

        setRequirements(
          Array.isArray(policy)
            ? policy
            : policy?.requirements ||
              []
        );
      } catch (requestError) {
        if (requestError.status === 401) {
          expireSession();
        }
      }
    },
    [sessionToken, expireSession]
  );

  useEffect(() => {
    let cancelled = false;

    async function initialise() {
      const token = invitationToken();

      let activeSession =
        window.localStorage.getItem(
          SESSION_KEY
        );

      try {
        let bootstrapData = null;

        /*
         * RAPID X PUBLIC ONBOARDING SESSION RESTORE
         *
         * A successfully bootstrapped invitation is one-time-use.
         * If this browser tab already has a candidate session,
         * restore that session instead of attempting to consume
         * the invitation bearer again.
         */
        /*
         * RAPIDX_PERSISTENT_CANDIDATE_SESSION_V1
         *
         * Reuse a saved session after the browser is reopened.
         * Only bootstrap the one-time invitation when no saved
         * candidate session is available.
         */
        if (!activeSession && token) {
          /*
           * IRIS V1.0C-H-R1-R3
           *
           * Fail locally before the public gateway if a
           * malformed/stale browser bundle ever presents an
           * invalid invitation credential.
           *
           * Never log or display the bearer itself.
           */
          if (
            typeof token !== "string" ||
            token.length < 32
          ) {
            throw new Error(
              "The secure invitation link is incomplete. " +
              "Please reopen the original invitation email."
            );
          }

          bootstrapData =
            await jsonRequest(
              BOOTSTRAP_URL,
              { token }
            );

          activeSession =
            bootstrapData?.session_token;

          /*
           * Remove the invitation bearer from the address bar
           * only AFTER the gateway has successfully returned a
           * candidate session.
           *
           * A failed network/API attempt must not destroy the
           * only recoverable copy of the one-time bearer.
           */
          if (activeSession) {
            clearInvitationFragment();
          }

          if (!activeSession) {
            throw new Error(
              "A secure onboarding session could not be created."
            );
          }

          window.localStorage.setItem(
            SESSION_KEY,
            activeSession
          );
        }

        if (!activeSession) {
          throw new Error(
            "No active onboarding invitation was found."
          );
        }

        const state =
          await jsonRequest(
            `${API}/state`,
            {
              session_token:
                activeSession,
            }
          );

        /*
         * Remove the original invitation bearer only after the
         * candidate session has passed server validation.
         */
        if (token) {
          clearInvitationFragment();
        }

        if (cancelled) return;

        setSessionToken(activeSession);

        setCandidate({
          candidate_name:
            state?.candidate_name ||
            bootstrapData?.candidate_name,
          candidate_email:
            state?.candidate_email ||
            bootstrapData?.candidate_email,
          contractor_name:
            state?.contractor_name ||
            bootstrapData?.contractor_name,
        });

        const step =
          state?.current_step ||
          state?.next_step ||
          "candidate_profile";

        setCurrentStep(step);

        const steps =
          state?.steps ||
          state?.draft?.steps ||
          {};

        if (steps.candidate_profile) {
          setProfile((previous) => ({
            ...previous,
            ...steps.candidate_profile,
          }));
        }

        if (steps.licences) {
          setLicences((previous) => ({
            ...previous,
            ...steps.licences,
          }));
        }

        if (steps.compliance) {
          setCompliance((previous) => ({
            ...previous,
            ...steps.compliance,
          }));
        }

        if (steps.declarations) {
          setDeclarations((previous) => ({
            ...previous,
            ...steps.declarations,
          }));
        }

        setStatus("ready");
        setMessage(
          "Complete the sections below to submit your onboarding information."
        );

        try {
          const [list, policy] =
            await Promise.all([
              jsonRequest(
                `${API}/evidence/list`,
                {
                  session_token:
                    activeSession,
                }
              ),
              jsonRequest(
                `${API}/evidence-requirements`,
                {
                  session_token:
                    activeSession,
                }
              ),
            ]);

          if (!cancelled) {
            setEvidence(
              Array.isArray(list)
                ? list
                : list?.evidence ||
                  list?.documents ||
                  []
            );

            setRequirements(
              Array.isArray(policy)
                ? policy
                : policy?.requirements ||
                  []
            );
          }
        } catch {
          // Evidence is refreshed again
          // when the candidate reaches licences.
        }
      } catch (initialError) {
        if (!cancelled) {
          window.localStorage.removeItem(
            SESSION_KEY
          );

          setStatus("error");
          setMessage(
            initialError?.message ||
            "The onboarding invitation could not be opened."
          );
        }
      }
    }

    initialise();

    return () => {
      cancelled = true;
    };
  }, []);

  function update(setter, key, value) {
    setter((previous) => ({
      ...previous,
      [key]: value,
    }));
  }

  async function saveStep(
    endpoint,
    payload,
    nextStep
  ) {
    setBusy(true);
    setError("");

    try {
      const result =
        await call(endpoint, payload);

      const serverStep =
        result?.current_step ||
        result?.next_step ||
        nextStep;

      setCurrentStep(
        serverStep || nextStep
      );

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      await refreshEvidence();
    } catch (saveError) {
      setError(
        saveError?.message ||
        "Your information could not be saved."
      );
    } finally {
      setBusy(false);
    }
  }

  async function openReview() {
    setBusy(true);
    setError("");

    try {
      await refreshEvidence();

      const result =
        await call("review");

      
        /*
         * IRIS_R7_R3_OPEN_REVIEW_ACK
         *
         * Review Step 4 is backed by the immutable
         * executed acknowledgement, not legacy declarations.
         */
        const acknowledgement =
          await reviewAcknowledgementRequest(
            sessionToken
          );

        if (!acknowledgement?.executed) {
          throw new Error(
            "Your subcontractor employee acknowledgement " +
            "has not been completed."
          );
        }

        setReviewAcknowledgement(
          acknowledgement
        );

        setReviewData(result);

      setCurrentStep(
        result?.current_step ||
        result?.next_step ||
        "review"
      );

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (reviewError) {
      setError(
        reviewError?.message ||
        "Your application is not ready for review."
      );
    } finally {
      setBusy(false);
    }
  }

  async function complete() {
    setBusy(true);
    setError("");

    try {
      await call(
        "complete",
        {
          confirm_submission: true,
        }
      );

      setCurrentStep("completed");

      window.localStorage.removeItem(
        SESSION_KEY
      );

      setSessionToken(null);
    } catch (completeError) {
      setError(
        completeError?.message ||
        "Your onboarding application could not be submitted."
      );
    } finally {
      setBusy(false);
    }
  }

  function previous() {
    const index =
      STEPS.indexOf(currentStep);

    if (index > 0) {
      setCurrentStep(
        STEPS[index - 1]
      );

      setError("");

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }

  function evidenceStatus(type) {
    const matching = evidence.filter(
      (item) =>
        item?.document_type === type
    );

    if (!matching.length) {
      return "Not uploaded";
    }

    const item = matching[0];

    return (
      item?.review_status ||
      item?.status ||
      "Uploaded"
    );
  }

  const requiredTypes =
    requirements
      .map((item) =>
        typeof item === "string"
          ? item
          : item?.document_type
      )
      .filter(Boolean);

  const button = {
    border: 0,
    borderRadius: "8px",
    padding: "12px 20px",
    fontSize: "15px",
    fontWeight: 700,
    cursor: busy
      ? "not-allowed"
      : "pointer",
  };

  if (status !== "ready") {
    return (
      <div
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(145deg,#07111f,#111827)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          boxSizing: "border-box",
          fontFamily:
            "Arial, Helvetica, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: "620px",
            width: "100%",
            background: "#fff",
            borderRadius: "16px",
            padding: "36px",
            boxSizing: "border-box",
            boxShadow:
              "0 24px 70px rgba(0,0,0,.35)",
          }}
        >
          <div
            style={{
              color: "#6b7280",
              textTransform: "uppercase",
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "1.5px",
            }}
          >
            {
              candidate?.contractor_name
              || "Contractor Onboarding"
            }
          </div>

          <h1
            style={{
              color: "#111827",
              marginBottom: "10px",
            }}
          >
            Contractor Onboarding
          </h1>

          <p
            style={{
              color: "#4b5563",
              lineHeight: 1.6,
            }}
          >
            {message}
          </p>

          {status === "initialising" && (
            <div
              style={{
                padding: "15px",
                background: "#f3f4f6",
                borderRadius: "8px",
              }}
            >
              Verifying secure invitation...
            </div>
          )}

          {status === "error" && (
            <div
              style={{
                padding: "15px",
                background: "#fef2f2",
                color: "#991b1b",
                borderRadius: "8px",
              }}
            >
              Please contact your contractor if
              you require a new onboarding
              invitation.
            </div>
          )}
        </div>
      </div>
    );
  }


  /*
   * IRIS V1.0C-H-R8-R3-R1
   *
   * Review must display the authoritative persisted
   * candidate draft returned by the backend, not stale
   * transient React form state.
   */
  /*
   * R3-R2-R1 fail-closed Review projection.
   *
   * Never display transient/default form state as though it
   * were the persisted candidate Review.
   */
  const authoritativeReview =
    reviewData?.canonical_review ||
    {};

  const reviewCandidateProfile =
    authoritativeReview.candidate_profile ||
    {};

  const reviewLicences =
    authoritativeReview.licences ||
    {};

  const reviewCompliance =
    authoritativeReview.compliance ||
    {};




  /*
   * IRIS V1.0C-H-R8-R3-R4-R2 evidence display normaliser
   *
   * Candidate-facing Review must not present a derived
   * "pending" requirement state as though an evidence
   * adjudication has taken place.
   *
   * Persisted/derived:
   *   null / empty / uploaded / pending -> Uploaded
   *
   * Explicit adjudication states remain visible.
   */
  function reviewEvidenceDisplayStatus(item) {
    const raw =
      item?.status ??
      item?.review_status ??
      "";

    const normalised =
      String(raw)
        .trim()
        .toLowerCase();

    if (
      !normalised ||
      normalised === "pending" ||
      normalised === "uploaded"
    ) {
      return "Uploaded";
    }

    const labels = {
      approved: "Approved",
      verified: "Verified",
      accepted: "Accepted",
      rejected: "Rejected",
      declined: "Declined",
      expired: "Expired",
      superseded: "Superseded",
    };

    return (
      labels[normalised] ||
      String(raw)
    );
  }


  /*
   * Review displays only the current, non-superseded
   * evidence version for each document type.
   * Historical evidence remains untouched in MongoDB.
   */
  const reviewEvidence = Object.values(
    (evidence || [])
      .filter(
        item =>
          !item?.superseded_at
      )
      .reduce(
        (latest, item) => {
          const type =
            item?.document_type ||
            item?.id;

          if (!type) {
            return latest;
          }

          const current =
            latest[type];

          const currentVersion =
            Number(
              current?.version || 0
            );

          const candidateVersion =
            Number(
              item?.version || 0
            );

          if (
            !current ||
            candidateVersion >=
              currentVersion
          ) {
            latest[type] = item;
          }

          return latest;
        },
        {}
      )
  );


  return (
    <>
    <div
      className="contractor-onboarding-public"
      style={{
        minHeight: "100vh",
        background: "#f3f4f6",
        fontFamily:
          "Arial, Helvetica, sans-serif",
        color: "#111827",
      }}
    >
      <header
        style={{
          background:
            "linear-gradient(135deg,#07111f,#172033)",
          color: "#fff",
          padding: "24px",
        }}
      >
        <div
          style={{
            maxWidth: "920px",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              letterSpacing: "1.6px",
              textTransform: "uppercase",
              opacity: 0.75,
              fontWeight: 700,
            }}
          >
            {
              candidate?.contractor_name
              || "Contractor Onboarding"
            }
          </div>

          <h1
            style={{
              margin: "6px 0",
              fontSize: "28px",
            }}
          >
            Contractor Onboarding
          </h1>

          <div
            style={{
              opacity: 0.8,
              fontSize: "14px",
            }}
          >
            Secure candidate onboarding portal
          </div>
        </div>
      </header>

      <main
        style={{
          maxWidth: "920px",
          margin: "0 auto",
          padding: "24px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: "14px",
            padding: "20px",
            marginBottom: "18px",
            boxShadow:
              "0 3px 15px rgba(0,0,0,.06)",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              gap: "15px",
              alignItems: "center",
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "17px",
                }}
              >
                {candidate?.candidate_name ||
                  "Candidate"}
              </div>

              <div
                style={{
                  color: "#6b7280",
                  fontSize: "14px",
                  marginTop: "3px",
                }}
              >
                {candidate?.candidate_email}
              </div>

              {candidate?.contractor_name && (
                <div
                  style={{
                    color: "#6b7280",
                    fontSize: "13px",
                    marginTop: "3px",
                  }}
                >
                  {candidate.contractor_name}
                </div>
              )}
            </div>

            <div
              style={{
                fontSize: "14px",
                color: "#374151",
                fontWeight: 600,
              }}
            >
              {progress}% complete
            </div>
          </div>

          <div
            style={{
              height: "7px",
              background: "#e5e7eb",
              borderRadius: "999px",
              overflow: "hidden",
              marginTop: "17px",
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: "100%",
                background: "#111827",
                transition: "width .25s",
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: "7px",
              flexWrap: "wrap",
              marginTop: "15px",
            }}
          >
            {STEPS.slice(0, 5).map(
              (step, index) => {
                const isCompleted =
                  index < activeIndex ||
                  currentStep === "completed";

                const isCurrent =
                  index === activeIndex &&
                  currentStep !== "completed";

                const isLocked =
                  index > activeIndex &&
                  currentStep !== "completed";

                return (
                  <div
                    key={step}
                    title={
                      isLocked
                        ? "Complete the previous step first"
                        : isCompleted
                          ? "Completed"
                          : "Current step"
                    }
                    aria-current={
                      isCurrent ? "step" : undefined
                    }
                    style={{
                      fontSize: "12px",
                      padding: "6px 9px",
                      borderRadius: "999px",
                      background:
                        isCompleted || isCurrent
                          ? "#111827"
                          : "#f3f4f6",
                      color:
                        isCompleted || isCurrent
                          ? "#fff"
                          : "#6b7280",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                      cursor: "default",
                      userSelect: "none",
                    }}
                  >
                    <span aria-hidden="true">
                      {isCompleted
                        ? "✓"
                        : isLocked
                          ? "🔒"
                          : index + 1}
                    </span>

                    <span>
                      {STEP_LABELS[step]}
                    </span>
                  </div>
                );
              }
            )}
          </div>
        </div>

        <div
          style={{
            background: "#fff",
            borderRadius: "14px",
            padding: "28px",
            boxShadow:
              "0 3px 15px rgba(0,0,0,.06)",
          }}
        >
          {error && (
            <div
              style={{
                padding: "13px 15px",
                borderRadius: "8px",
                background: "#fef2f2",
                color: "#991b1b",
                marginBottom: "20px",
                lineHeight: 1.5,
              }}
            >
              {error}
            </div>
          )}

          {currentStep ===
            "candidate_profile" && (
            <>
              <SectionTitle
                title="Your details"
                description="Enter your current personal and emergency contact information."
              />

              <Field
                label="Legal name"
                required
                value={profile.legal_name}
                onChange={(value) =>
                  update(
                    setProfile,
                    "legal_name",
                    value
                  )
                }
              />

              <Field
                label="Preferred name"
                value={profile.preferred_name}
                onChange={(value) =>
                  update(
                    setProfile,
                    "preferred_name",
                    value
                  )
                }
              />

              <Field
                label="Mobile phone"
                required
                value={profile.mobile_phone}
                onChange={(value) =>
                  update(
                    setProfile,
                    "mobile_phone",
                    value
                  )
                }
              />

              <Field
                label="Residential address"
                required
                value={
                  profile.residential_address
                }
                onChange={(value) =>
                  update(
                    setProfile,
                    "residential_address",
                    value
                  )
                }
              />

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit,minmax(150px,1fr))",
                  gap: "14px",
                }}
              >
                <Field
                  label="Suburb"
                  required
                  value={profile.suburb}
                  onChange={(value) =>
                    update(
                      setProfile,
                      "suburb",
                      value
                    )
                  }
                />

                <Field
                  label="State"
                  required
                  value={profile.state}
                  onChange={(value) =>
                    update(
                      setProfile,
                      "state",
                      value
                    )
                  }
                />

                <Field
                  label="Postcode"
                  required
                  value={profile.postcode}
                  onChange={(value) =>
                    update(
                      setProfile,
                      "postcode",
                      value
                    )
                  }
                />
              </div>

              <Field
                label="Emergency contact name"
                required
                value={
                  profile.emergency_contact_name
                }
                onChange={(value) =>
                  update(
                    setProfile,
                    "emergency_contact_name",
                    value
                  )
                }
              />

              <Field
                label="Emergency contact phone"
                required
                value={
                  profile.emergency_contact_phone
                }
                onChange={(value) =>
                  update(
                    setProfile,
                    "emergency_contact_phone",
                    value
                  )
                }
              />

              <div
                style={{
                  textAlign: "right",
                }}
              >
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    saveStep(
                      "candidate-profile",
                      profile,
                      "licences"
                    )
                  }
                  style={{
                    ...button,
                    background: "#111827",
                    color: "#fff",
                  }}
                >
                  {busy
                    ? "Saving..."
                    : "Save and continue"}
                </button>
              </div>
            </>
          )}

          {currentStep === "licences" && (
            <>
              <SectionTitle
                title="Licences and qualifications"
                description="Provide your current security licence and relevant qualifications."
              />

              <Field
                label="Security licence number"
                required
                value={
                  licences.security_licence_number
                }
                onChange={(value) =>
                  update(
                    setLicences,
                    "security_licence_number",
                    value
                  )
                }
              />

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit,minmax(200px,1fr))",
                  gap: "14px",
                }}
              >
                <Field
                  label="Security licence state"
                  required
                  value={
                    licences.security_licence_state
                  }
                  onChange={(value) =>
                    update(
                      setLicences,
                      "security_licence_state",
                      value
                    )
                  }
                />

                <Field
                  label="Security licence expiry"
                  type="date"
                  required
                  value={
                    licences.security_licence_expiry
                  }
                  onChange={(value) =>
                    update(
                      setLicences,
                      "security_licence_expiry",
                      value
                    )
                  }
                />
              </div>

              <UploadBox
                sessionToken={sessionToken}
                documentType="security_licence"
                title="Security licence evidence"
                required
                description={`PDF, JPG or PNG. Current status: ${evidenceStatus(
                  "security_licence"
                )}`}
                onUploaded={refreshEvidence}
              />

              <div
                style={{
                  margin: "25px 0 12px",
                  borderTop:
                    "1px solid #e5e7eb",
                  paddingTop: "20px",
                }}
              >
                <Check
                  label="I hold a current driver licence"
                  checked={
                    licences.drivers_licence_held
                  }
                  onChange={(value) =>
                    update(
                      setLicences,
                      "drivers_licence_held",
                      value
                    )
                  }
                />

                {licences.drivers_licence_held && (
                  <>
                    <Field
                      label="Driver licence number"
                      required
                      value={
                        licences.drivers_licence_number
                      }
                      onChange={(value) =>
                        update(
                          setLicences,
                          "drivers_licence_number",
                          value
                        )
                      }
                    />

                    <UploadBox
                      sessionToken={
                        sessionToken
                      }
                      documentType="drivers_licence"
                      title="Driver licence evidence"
                      required
                      description={`PDF, JPG or PNG. Current status: ${evidenceStatus(
                        "drivers_licence"
                      )}`}
                      onUploaded={
                        refreshEvidence
                      }
                    />
                  </>
                )}
              </div>

              <div
                style={{
                  margin: "25px 0 12px",
                  borderTop:
                    "1px solid #e5e7eb",
                  paddingTop: "20px",
                }}
              >
                <Check
                  label="I hold current first aid certification"
                  checked={
                    licences.first_aid_current
                  }
                  onChange={(value) =>
                    update(
                      setLicences,
                      "first_aid_current",
                      value
                    )
                  }
                />

                {licences.first_aid_current && (
                  <>
                    <Field
                      label="First aid expiry"
                      type="date"
                      required
                      value={
                        licences.first_aid_expiry
                      }
                      onChange={(value) =>
                        update(
                          setLicences,
                          "first_aid_expiry",
                          value
                        )
                      }
                    />

                    <UploadBox
                      sessionToken={
                        sessionToken
                      }
                      documentType="first_aid_certificate"
                      title="First aid certificate"
                      required
                      description={`PDF, JPG or PNG. Current status: ${evidenceStatus(
                        "first_aid_certificate"
                      )}`}
                      onUploaded={
                        refreshEvidence
                      }
                    />
                  </>
                )}
              </div>

              <div
                style={{
                  marginTop: "20px",
                  display: "flex",
                  justifyContent:
                    "space-between",
                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  onClick={previous}
                  style={{
                    ...button,
                    background: "#e5e7eb",
                    color: "#111827",
                  }}
                >
                  Back
                </button>

                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    saveStep(
                      "licences",
                      licences,
                      "compliance"
                    )
                  }
                  style={{
                    ...button,
                    background: "#111827",
                    color: "#fff",
                  }}
                >
                  {busy
                    ? "Saving..."
                    : "Save and continue"}
                </button>
              </div>
            </>
          )}

          {currentStep === "compliance" && (
            <>
              <SectionTitle
                title="Compliance"
                description="Confirm your work eligibility and disclose information relevant to your engagement."
              />

              <Check
                label="I confirm that I have the legal right to work in Australia."
                checked={
                  compliance.right_to_work_confirmed
                }
                onChange={(value) =>
                  update(
                    setCompliance,
                    "right_to_work_confirmed",
                    value
                  )
                }
              />

              <UploadBox
                sessionToken={sessionToken}
                documentType="right_to_work"
                title="Right-to-work evidence"
                required={requiredTypes.includes(
                  "right_to_work"
                )}
                description={`Optional unless requested. Current status: ${evidenceStatus(
                  "right_to_work"
                )}`}
                onUploaded={refreshEvidence}
              />

              <Check
                label="I agree to follow all site rules and lawful site instructions."
                checked={
                  compliance.willing_to_follow_site_rules
                }
                onChange={(value) =>
                  update(
                    setCompliance,
                    "willing_to_follow_site_rules",
                    value
                  )
                }
              />

              <Check
                label="I agree to comply with workplace health and safety requirements."
                checked={
                  compliance.willing_to_follow_whs_requirements
                }
                onChange={(value) =>
                  update(
                    setCompliance,
                    "willing_to_follow_whs_requirements",
                    value
                  )
                }
              />

              <Check
                label="I confirm that I am fit to perform the duties assigned to me."
                checked={
                  compliance.fit_for_assigned_duties
                }
                onChange={(value) =>
                  update(
                    setCompliance,
                    "fit_for_assigned_duties",
                    value
                  )
                }
              />




              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  gap: "10px",
                  marginTop: "20px",
                }}
              >
                <button
                  type="button"
                  onClick={previous}
                  style={{
                    ...button,
                    background: "#e5e7eb",
                    color: "#111827",
                  }}
                >
                  Back
                </button>

                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    saveStep(
                      "compliance",
                      compliance,
                      "declarations"
                    )
                  }
                  style={{
                    ...button,
                    background: "#111827",
                    color: "#fff",
                  }}
                >
                  {busy
                    ? "Saving..."
                    : "Save and continue"}
                </button>
              </div>
            </>
          )}

          {currentStep === "declarations" && (
              <>
                <SectionTitle
                  title="Acknowledgement & Indemnity"
                  description="Read and electronically sign the required subcontractor employee acknowledgement before reviewing your application."
                />

                <SubcontractorAcknowledgementGate
                  sessionToken={sessionToken}
                  currentStep={currentStep}
                  onExecuted={openReview}
                />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    marginTop: "20px",
                  }}
                >
                  <button
                    type="button"
                    onClick={previous}
                    style={{
                      ...button,
                      background: "#e5e7eb",
                      color: "#111827",
                    }}
                  >
                    Back
                  </button>
                </div>
              </>
            )}

            {currentStep === "review" && (
            <>
              <SectionTitle
                title="Review and submit"
                description="Review your onboarding information before final submission."
              />

              {/* IRIS_R3_R2_R1_REVIEW_LOAD_STATE */}
              {!reviewData?.canonical_review && (
                <div
                  style={{
                    marginTop: "14px",
                    marginBottom: "14px",
                    padding: "12px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    lineHeight: 1.5,
                    background:
                      reviewData?.__review_load_error
                        ? "#fef2f2"
                        : "#eff6ff",
                    border:
                      reviewData?.__review_load_error
                        ? "1px solid #fecaca"
                        : "1px solid #bfdbfe",
                    color:
                      reviewData?.__review_load_error
                        ? "#991b1b"
                        : "#1e3a8a",
                  }}
                >
                  {
                    reviewData?.__review_load_error
                      ? reviewData.__review_load_error
                      : "Loading your saved onboarding information..."
                  }
                </div>
              )}


              <div
                style={{
                  display: "grid",
                  gap: "14px",
                }}
              >
                {[
                  [
                    "Candidate details",
                    reviewCandidateProfile,
                  ],
                  [
                    "Licences",
                    reviewLicences,
                  ],
                  [
                    "Compliance",
                    reviewCompliance,
                  ],
                ].map(([title, data]) => (
                  <div
                    key={title}
                    style={{
                      border:
                        "1px solid #e5e7eb",
                      borderRadius: "10px",
                      padding: "17px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        marginBottom: "10px",
                      }}
                    >
                      {title}
                    </div>

                    {Object.entries(
                      data
                    ).map(
                      ([key, value]) => (
                        <div
                          key={key}
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "minmax(150px, 1fr) 2fr",
                            gap: "10px",
                            padding: "5px 0",
                            fontSize: "13px",
                            borderBottom:
                              "1px solid #f3f4f6",
                          }}
                        >
                          <span
                            style={{
                              color:
                                "#6b7280",
                            }}
                          >
                            {key
                              .replaceAll(
                                "_",
                                " "
                              )}
                          </span>

                          <span>
                            {typeof value ===
                            "boolean"
                              ? value
                                ? "Yes"
                                : "No"
                              : value ||
                                "—"}
                          </span>
                        </div>
                      )
                    )}
                  </div>
                ))}

                {/* IRIS_R7_R3_R4_ACK_REVIEW_CARD */}
                <div
                  style={{
                    border:
                      "1px solid #e5e7eb",
                    borderRadius:
                      "10px",
                    padding:
                      "17px",
                  }}
                >
                  <div
                    style={{
                      fontWeight:
                        700,
                      marginBottom:
                        "10px",
                    }}
                  >
                    Acknowledgement &amp; Indemnity
                  </div>

                  {reviewAcknowledgement?.executed ? (
                    <>
                      {[
                        [
                          "Status",
                          "Signed",
                        ],
                        [
                          "Employee",
                          reviewAcknowledgement
                            ?.candidate_legal_name ||
                            "—",
                        ],
                        [
                          "Employer (Subcontractor)",
                          reviewAcknowledgement
                            ?.employer_legal_name ||
                            reviewAcknowledgement
                              ?.contractor_name ||
                            "—",
                        ],
                        [
                          "Employer ABN",
                          reviewAcknowledgement
                            ?.employer_abn ||
                            "—",
                        ],
                        [
                          "Electronic signature",
                          reviewAcknowledgement
                            ?.signature_name ||
                            "—",
                        ],
                        [
                          "Document version",
                          reviewAcknowledgement
                            ?.document_version ||
                            "—",
                        ],
                      ].map(
                        ([label, value]) => (
                          <div
                            key={label}
                            style={{
                              display:
                                "grid",
                              gridTemplateColumns:
                                "minmax(150px, 1fr) 2fr",
                              gap:
                                "10px",
                              padding:
                                "5px 0",
                              fontSize:
                                "13px",
                              borderBottom:
                                "1px solid #f3f4f6",
                            }}
                          >
                            <span
                              style={{
                                color:
                                  "#6b7280",
                              }}
                            >
                              {label}
                            </span>

                            <span>
                              {value}
                            </span>
                          </div>
                        )
                      )}

                      <div
                        style={{
                          marginTop:
                            "12px",
                          padding:
                            "10px 12px",
                          background:
                            "#f0fdf4",
                          color:
                            "#166534",
                          borderRadius:
                            "8px",
                          fontSize:
                            "13px",
                          lineHeight:
                            1.5,
                        }}
                      >
                        Subcontractor Employee
                        Acknowledgement and Indemnity
                        executed successfully.
                      </div>
                    </>
                  ) : (
                    <div
                      style={{
                        background:
                          "#fef2f2",
                        color:
                          "#991b1b",
                        borderRadius:
                          "8px",
                        padding:
                          "10px 12px",
                        fontSize:
                          "13px",
                      }}
                    >
                      Executed acknowledgement
                      could not be verified.
                    </div>
                  )}
                </div>

                <div
                  style={{
                    border:
                      "1px solid #e5e7eb",
                    borderRadius: "10px",
                    padding: "17px",
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      marginBottom: "10px",
                    }}
                  >
                    Uploaded evidence
                  </div>

                  {reviewEvidence.length === 0 ? (
                    <div
                      style={{
                        color: "#6b7280",
                      }}
                    >
                      No evidence is currently
                      listed.
                    </div>
                  ) : (
                    reviewEvidence.map(
                      (item, index) => (
                        <div
                          key={
                            item?.id ||
                            index
                          }
                          style={{
                            padding:
                              "7px 0",
                            borderBottom:
                              "1px solid #f3f4f6",
                            fontSize:
                              "14px",
                          }}
                        >
                          <strong>
                            {item?.document_type ||
                              "Document"}
                          </strong>
                          {" — "}
                          {reviewEvidenceDisplayStatus(
                              item
                            )}
                        </div>
                      )
                    )
                  )}
                </div>
              </div>

              {reviewData?.message && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "13px",
                    background: "#f0fdf4",
                    color: "#166534",
                    borderRadius: "8px",
                  }}
                >
                  {reviewData.message}
                </div>
              )}

              <div
                style={{
                  marginTop: "22px",
                  padding: "15px",
                  background: "#fffbeb",
                  color: "#78350f",
                  borderRadius: "8px",
                  fontSize: "14px",
                  lineHeight: 1.55,
                }}
              >
                By submitting, you confirm
                that this onboarding
                information is complete and
                ready for contractor review
                and BSMS principal assessment.
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  gap: "10px",
                  marginTop: "22px",
                }}
              >
                <button
                  type="button"
                  onClick={previous}
                  style={{
                    ...button,
                    background: "#e5e7eb",
                    color: "#111827",
                  }}
                >
                  Back
                </button>

                <button
                  type="button"
                  disabled={
                    busy ||
                    !reviewData?.canonical_review ||
                    !reviewAcknowledgement?.executed
                  }
                  onClick={complete}
                  style={{
                    ...button,
                    background:
                      (
                        busy ||
                        !reviewData?.canonical_review ||
                        !reviewAcknowledgement?.executed
                      )
                        ? "#9ca3af"
                        : "#15803d",
                    cursor:
                      (
                        busy ||
                        !reviewData?.canonical_review ||
                        !reviewAcknowledgement?.executed
                      )
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      (
                        busy ||
                        !reviewData?.canonical_review ||
                        !reviewAcknowledgement?.executed
                      )
                        ? 0.72
                        : 1,
                  }}
                  /* IRIS_R7_R4_SUBMIT_DISABLED_STYLE */
                >
                  {busy
                    ? "Submitting..."
                    : "Submit onboarding"}
                </button>
              </div>
            </>
          )}

          {currentStep === "completed" && (
            <div
              style={{
                textAlign: "center",
                padding: "25px 10px",
              }}
            >
              <div
                style={{
                  width: "58px",
                  height: "58px",
                  margin: "0 auto 18px",
                  borderRadius: "50%",
                  background: "#dcfce7",
                  color: "#166534",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "28px",
                  fontWeight: 700,
                }}
              >
                ✓
              </div>

              <h2
                style={{
                  marginBottom: "10px",
                }}
              >
                Onboarding submitted
              </h2>

              <p
                style={{
                  color: "#4b5563",
                  lineHeight: 1.6,
                  maxWidth: "560px",
                  margin: "0 auto",
                }}
              >
                Thank you. Your onboarding
                information has been
                submitted to {
                  candidate?.contractor_name
                  || "your contractor"
                } for review. You may now
                close this window.
              </p>
            </div>
          )}
        </div>

        <div
          style={{
            textAlign: "center",
            padding: "20px",
            color: "#6b7280",
            fontSize: "12px",
          }}
        >
          Secure contractor onboarding • {
            candidate?.contractor_name
            || "Contractor Onboarding"
          }
        </div>
      </main>
    </div>
    </>
  );
}
